import { useState, useEffect } from "react";
import { Stack, Text, Group, Select, Box, Button } from "@mantine/core";
import { useChainJointsResult, useViewerStore } from "../../store";
import { useWebSocket } from "../../WebSocket";

// Kinematic chain selector (base link → tip link)
export function ChainSelector({
  availableLinks,
  onChainSelected,
}: {
  availableLinks: string[];
  onChainSelected: (joints: string[]) => void;
}) {
  const [baseLink, setBaseLink] = useState<string | null>(null);
  const [tipLink, setTipLink] = useState<string | null>(null);
  const chainJointsResult = useChainJointsResult();
  const clearChainJointsResult = useViewerStore((state) => state.clearChainJointsResult);
  const { sendGetChainJoints } = useWebSocket();

  // Request chain joints when both links are selected
  useEffect(() => {
    if (baseLink && tipLink) {
      sendGetChainJoints(baseLink, tipLink);
    }
  }, [baseLink, tipLink, sendGetChainJoints]);

  // Handle chain result
  useEffect(() => {
    if (chainJointsResult && chainJointsResult.success && chainJointsResult.jointNames.length > 0) {
      // Don't auto-apply, just show the result
    }
  }, [chainJointsResult]);

  const handleApplyChain = () => {
    if (chainJointsResult && chainJointsResult.success) {
      onChainSelected(chainJointsResult.jointNames);
      clearChainJointsResult();
    }
  };

  const linkOptions = availableLinks.map((link) => ({ value: link, label: link }));

  return (
    <Stack gap="sm">
      <Text size="xs" c="dimmed">
        Select base and tip links to automatically find all joints in the kinematic chain.
      </Text>

      <Group grow>
        <Select
          label="Base Link"
          placeholder="Select start link"
          size="xs"
          data={linkOptions}
          value={baseLink}
          onChange={setBaseLink}
          searchable
          clearable
        />
        <Select
          label="Tip Link"
          placeholder="Select end link"
          size="xs"
          data={linkOptions}
          value={tipLink}
          onChange={setTipLink}
          searchable
          clearable
        />
      </Group>

      {/* Chain result display */}
      {chainJointsResult && (
        <Box
          p="xs"
          style={{
            border: chainJointsResult.success ? "1px solid var(--mantine-color-green-3)" : "1px solid var(--mantine-color-red-3)",
            borderRadius: 4,
            backgroundColor: chainJointsResult.success ? "var(--mantine-color-green-0)" : "var(--mantine-color-red-0)",
          }}
        >
          {chainJointsResult.success ? (
            <>
              <Text size="xs" fw={500} c="green">Found {chainJointsResult.jointNames.length} joints in chain:</Text>
              <Text size="xs" c="dimmed" mt={4}>
                {chainJointsResult.jointNames.map((j) => j.split("/").pop()).join(" → ")}
              </Text>
              <Button size="xs" mt="xs" onClick={handleApplyChain}>
                Use These Joints
              </Button>
            </>
          ) : (
            <Text size="xs" c="red">{chainJointsResult.error || "No chain found"}</Text>
          )}
        </Box>
      )}
    </Stack>
  );
}

